import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, EnvelopeSimple, Star } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import CheckoutTermsDialog from "@/components/CheckoutTermsDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pricing v4 — un'unica scala per business + agency
const PLAN_ORDER = ["start", "pro", "scale"];
const HIGHLIGHT_PLAN = "pro";

// Workspace add-on tiers (visibili solo agli account agency)
const WS_TIER_ORDER = ["ws_solo", "ws_team", "ws_scale"];
const WS_TIER_LABELS = {
  ws_solo: { name: "Solo", emp: "fino a 20 dipendenti", price: 15 },
  ws_team: { name: "Team", emp: "21–100 dipendenti", price: 40 },
  ws_scale: { name: "Scale", emp: "101–250 dipendenti", price: 100 },
};

function formatPrice(cents, period) {
  if (cents == null) return { main: "—", sub: "", note: null };
  const euros = cents / 100;
  if (period === "annual") {
    const monthly = euros / 12;
    return { main: `${Math.round(monthly)}€`, sub: "/mese", note: `${Math.round(euros)}€ all'anno` };
  }
  return { main: `${Math.round(euros)}€`, sub: "/mese", note: null };
}

export default function PricingPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [enterpriseEmail, setEnterpriseEmail] = useState("sales@selly-hr.it");
  const [period, setPeriod] = useState("annual");
  const [busy, setBusy] = useState(null);
  // B71-QUAT TASK 4: dialog di riconferma legale prima del checkout
  const [pendingPlan, setPendingPlan] = useState(null);

  useEffect(() => {
    axios.get(`${API}/billing/plans`, { withCredentials: true }).then((r) => {
      setPlans(r.data.plans);
      setEnterpriseEmail(r.data.enterprise_contact_email || "sales@selly-hr.it");
    }).catch(() => {});
  }, []);

  const checkout = (planCode) => {
    // Apre il dialog legale; il pagamento parte solo dopo `onConfirm`
    setPendingPlan(planCode);
  };

  const confirmAndCheckout = async ({ accept_terms_checkout, accept_vexatious, doc_version }) => {
    const planCode = pendingPlan;
    if (!planCode) return;
    setBusy(planCode);
    try {
      const { data } = await axios.post(
        `${API}/billing/checkout`,
        {
          plan_id: planCode,
          billing_period: period,
          origin_url: window.location.origin,
          accept_terms_checkout,
          accept_vexatious,
          doc_version,
        },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel checkout");
      setBusy(null);
      setPendingPlan(null);
    }
  };

  const currentCode = user?.plan_code;
  const trialCta = !user || user?.plan_code === "trial";
  const isAgency = user?.account_type === "agency";

  const renderFeatures = (p) => {
    const labels = p.feature_labels || [];
    return (
      <ul className="space-y-1.5 mt-4" data-testid="feature-list">
        {labels.map((l, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#0D1F3C]/80">
            <Check size={16} weight="bold" className="text-[#1B4FD8] flex-shrink-0 mt-0.5" />
            <span>{l}</span>
          </li>
        ))}
      </ul>
    );
  };

  const renderCard = (code) => {
    if (!plans || !plans[code]) return null;
    const p = plans[code];
    const price = formatPrice(period === "annual" ? p.price_annual : p.price_monthly, period);
    const isCurrent = currentCode === code;
    const isHighlighted = code === HIGHLIGHT_PLAN;
    const unitsLabel = p.tests_per_month >= 9999
      ? "Analisi illimitate (fair-use)"
      : `${p.tests_per_month} analisi e generazioni/mese`;
    return (
      <Card
        key={code}
        data-testid={`plan-card-${code}`}
        className={`border-[#0D1F3C]/10 relative ${isHighlighted ? "border-[#1B4FD8] shadow-xl scale-[1.02]" : ""}`}
      >
        {isHighlighted && (
          <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1B4FD8] text-white flex items-center gap-1 px-3">
            <Star size={12} weight="fill" /> più scelto
          </Badge>
        )}
        <CardContent className="p-6">
          <h3 className="text-xl font-bold text-[#0D1F3C]">{p.name}</h3>
          <div className="mt-3">
            <span className="text-4xl font-bold text-[#1B4FD8]">{price.main}</span>
            <span className="text-sm font-normal text-[#0D1F3C]/50">{price.sub}</span>
          </div>
          {price.note && <p className="text-xs text-[#0D1F3C]/50 mt-1">{price.note}</p>}
          <p className="text-sm font-medium text-[#0D1F3C] mt-4">{unitsLabel}</p>
          {renderFeatures(p)}
          <div className="mt-6">
            {isCurrent ? (
              <Button disabled className="w-full bg-[#0D1F3C]/20 text-[#0D1F3C]" data-testid={`plan-current-${code}`}>
                Piano attivo
              </Button>
            ) : (
              <Button
                onClick={() => checkout(code)}
                disabled={busy === code}
                data-testid={`plan-checkout-${code}`}
                className={`w-full ${isHighlighted ? "bg-[#1B4FD8] hover:bg-[#0D1F3C]" : "bg-[#0D1F3C] hover:bg-[#1B4FD8]"}`}
              >
                {busy === code ? "..." : "Scegli questo piano"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderEnterpriseCard = () => {
    const p = plans?.enterprise;
    if (!p) return null;
    return (
      <Card className="border-[#0D1F3C]/10 bg-gradient-to-br from-[#0D1F3C] to-[#1B4FD8] text-white" data-testid="plan-card-enterprise">
        <CardContent className="p-6">
          <h3 className="text-xl font-bold">{p.name || "Oltre Scale"}</h3>
          <p className="text-sm text-white/70 mt-2">Quote analisi e generazioni su misura per esigenze enterprise.</p>
          <ul className="space-y-1.5 mt-4">
            {(p.feature_labels || []).map((l, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-white/90">
                <Check size={16} weight="bold" className="text-[#56A3F5] flex-shrink-0 mt-0.5" />
                <span>{l}</span>
              </li>
            ))}
          </ul>
          <Button
            onClick={() => (window.location.href = `mailto:${enterpriseEmail}?subject=Richiesta%20Oltre%20Scale%20SELLY`)}
            data-testid="plan-contact-enterprise"
            className="w-full mt-6 bg-white text-[#0D1F3C] hover:bg-white/90 flex items-center gap-2"
          >
            <EnvelopeSimple size={18} /> Contatta vendite
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="animate-fade-in" data-testid="pricing-page">
      <PageHeader title="Piani e prezzi" />

      {trialCta && (
        <Card className="border-[#1B4FD8]/30 bg-[#56A3F5]/10 mb-6" data-testid="trial-cta">
          <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-[#0D1F3C]">Inizia il Trial 14 giorni</h3>
              <p className="text-sm text-[#0D1F3C]/70">10 analisi e generazioni incluse, nessuna carta richiesta. Vedi Selly all'opera sui tuoi candidati.</p>
            </div>
            <Button className="bg-[#1B4FD8] hover:bg-[#0D1F3C]" data-testid="start-trial-btn" onClick={() => (window.location.href = user ? "/candidates" : "/register")}>
              {user ? "Vai al trial" : "Inizia gratis"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Period toggle */}
      <div className="flex items-center gap-3 mb-8">
        <div className="inline-flex bg-white border border-[#0D1F3C]/10 rounded-full p-1" data-testid="period-toggle">
          <button
            onClick={() => setPeriod("monthly")}
            data-testid="period-monthly"
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${period === "monthly" ? "bg-[#0D1F3C] text-white" : "text-[#0D1F3C]/60"}`}
          >
            Mensile
          </button>
          <button
            onClick={() => setPeriod("annual")}
            data-testid="period-annual"
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${period === "annual" ? "bg-[#0D1F3C] text-white" : "text-[#0D1F3C]/60"}`}
          >
            Annuale
          </button>
        </div>
        {period === "annual" && (
          <Badge className="bg-green-100 text-green-800 border border-green-300">−25% sul mensile</Badge>
        )}
      </div>

      {/* Plans grid v4 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="plans-grid">
        {PLAN_ORDER.map(renderCard)}
      </div>

      {/* Enterprise */}
      <div className="mt-6 max-w-md">{renderEnterpriseCard()}</div>

      {/* Workspace add-on (solo agency) */}
      {isAgency && (
        <div className="mt-12" data-testid="agency-addon-section">
          <h2 className="text-2xl font-bold text-[#0D1F3C] mb-2">Workspace clienti — add-on Agenzia</h2>
          <p className="text-sm text-[#0D1F3C]/70 mb-6 max-w-2xl">
            Il piano principale copre le tue analisi e generazioni. Per ogni cliente gestito paghi un add-on workspace
            in base alla loro dimensione. Le unità restano nel pool del piano principale. White-label PDF attivo
            automaticamente dal primo cliente.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {WS_TIER_ORDER.map((code) => {
              const t = WS_TIER_LABELS[code];
              return (
                <Card key={code} className="border-[#0D1F3C]/10" data-testid={`ws-tier-${code}`}>
                  <CardContent className="p-5">
                    <h3 className="text-lg font-bold text-[#0D1F3C]">{t.name}</h3>
                    <p className="text-xs text-[#0D1F3C]/60 mt-1">{t.emp}</p>
                    <div className="mt-3">
                      <span className="text-2xl font-bold text-[#1B4FD8]">{t.price}€</span>
                      <span className="text-xs text-[#0D1F3C]/50">/mese · cliente</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <p className="text-xs text-[#0D1F3C]/50 mt-3">
            Oltre 250 dipendenti per cliente: su misura, contatta vendite. Upgrade automatico al tier successivo
            al superamento della soglia, con preavviso 14 giorni.
          </p>
        </div>
      )}

      {/* Overage note */}
      <p className="text-xs text-[#0D1F3C]/50 mt-8" data-testid="overage-note">
        Analisi e generazioni oltre la quota mensile: <strong>€1,50/unità extra</strong> fatturate a fine mese.
        Nessun blocco del servizio.
      </p>
      <p className="text-xs text-[#0D1F3C]/40 mt-2">
        Contratto annuale (12 mesi), addebito mensile automatico.
      </p>
      {/* B71-QUAT TASK 4: dialog riconferma legale al checkout */}
      <CheckoutTermsDialog
        open={!!pendingPlan}
        onClose={() => { if (!busy) setPendingPlan(null); }}
        onConfirm={confirmAndCheckout}
        busy={!!busy}
      />
    </div>
  );
}
